/**
 * Janus job taxonomy — bilingual (PT default + EN).
 *
 * The canonical owner is operations-management (`/api/v1/taxonomy`, served
 * to the app via the BFF `/api/v1/config/taxonomy`). These constants are the
 * OFFLINE FALLBACK so the ICP selector / contact badges still render if the
 * catalog endpoint is unreachable. They MUST stay key-aligned with the
 * ops-mgmt seed (`deploy/seed_taxonomy.py`). Labels can be refreshed live
 * from the endpoint via `useJanusTaxonomy()` without a frontend deploy.
 *
 * Portuguese (`labelPt`) is the platform default; `labelEn` is the English
 * equivalent. `getSeniorityLabel` / `getDeptLabel` default to PT.
 */

export type TaxonomyLang = 'pt' | 'en';

export interface SeniorityEntry {
  key: string;
  labelPt: string;
  labelEn: string;
  isDecisionMaker: boolean;
}

export interface DepartmentEntry {
  key: string;
  labelPt: string;
  labelEn: string;
  icpSelectable: boolean;
}

export const JANUS_SENIORITIES: readonly SeniorityEntry[] = [
  { key: 'owner', labelPt: 'Proprietário / Dono', labelEn: 'Owner', isDecisionMaker: true },
  { key: 'founder', labelPt: 'Fundador / Co-fundador', labelEn: 'Founder / Co-founder', isDecisionMaker: true },
  { key: 'partner', labelPt: 'Sócio / Partner', labelEn: 'Partner', isDecisionMaker: true },
  { key: 'c_suite', labelPt: 'C-Level (CEO, CTO, CFO…)', labelEn: 'C-Level (CEO, CTO, CFO…)', isDecisionMaker: true },
  { key: 'vice_president', labelPt: 'Vice-Presidente (VP)', labelEn: 'Vice President (VP)', isDecisionMaker: true },
  { key: 'head', labelPt: 'Head / Lead', labelEn: 'Head / Lead', isDecisionMaker: true },
  { key: 'director', labelPt: 'Diretor(a)', labelEn: 'Director', isDecisionMaker: true },
  { key: 'manager', labelPt: 'Gerente', labelEn: 'Manager', isDecisionMaker: true },
  { key: 'senior', labelPt: 'Sênior / Especialista', labelEn: 'Senior / Specialist', isDecisionMaker: false },
  { key: 'entry', labelPt: 'Pleno / Júnior', labelEn: 'Mid / Junior', isDecisionMaker: false },
  { key: 'intern', labelPt: 'Estagiário(a)', labelEn: 'Intern', isDecisionMaker: false },
  { key: 'other', labelPt: 'Outro', labelEn: 'Other', isDecisionMaker: false },
] as const;

// ICP selector uses only the icpSelectable departments — c_level is display-only
// (executive level is captured by the seniority dimension: c_suite, vice_president, etc.)
export const JANUS_DEPARTMENTS: readonly DepartmentEntry[] = [
  { key: 'sales', labelPt: 'Vendas / Comercial', labelEn: 'Sales', icpSelectable: true },
  { key: 'marketing', labelPt: 'Marketing', labelEn: 'Marketing', icpSelectable: true },
  { key: 'technology', labelPt: 'TI / Tecnologia', labelEn: 'IT / Technology', icpSelectable: true },
  { key: 'engineering', labelPt: 'Engenharia / Produto Tech', labelEn: 'Engineering / Tech Product', icpSelectable: true },
  { key: 'finance', labelPt: 'Financeiro / Contabilidade', labelEn: 'Finance / Accounting', icpSelectable: true },
  { key: 'hr', labelPt: 'RH / Recursos Humanos', labelEn: 'HR / Human Resources', icpSelectable: true },
  { key: 'operations', labelPt: 'Operações / Logística', labelEn: 'Operations / Logistics', icpSelectable: true },
  { key: 'legal', labelPt: 'Jurídico / Compliance', labelEn: 'Legal / Compliance', icpSelectable: true },
  { key: 'product', labelPt: 'Produto', labelEn: 'Product', icpSelectable: true },
  { key: 'consulting', labelPt: 'Consultoria', labelEn: 'Consulting', icpSelectable: true },
  { key: 'c_level', labelPt: 'Diretoria Executiva', labelEn: 'Executive Leadership', icpSelectable: false },
  { key: 'other', labelPt: 'Outro', labelEn: 'Other', icpSelectable: false },
] as const;

// ── Live override (from the central ops-mgmt catalog via BFF) ─────────────
// `useJanusTaxonomy()` populates these maps once the `/api/v1/config/taxonomy`
// fetch resolves, so a label edit in ops-mgmt propagates without a frontend
// deploy. Seeded from the constants above so labels resolve even before (or
// without) the fetch.
type LabelPair = { labelPt: string; labelEn: string };

const seniorityMap = new Map<string, LabelPair>(
  JANUS_SENIORITIES.map((s) => [s.key, { labelPt: s.labelPt, labelEn: s.labelEn }]),
);
const departmentMap = new Map<string, LabelPair>(
  JANUS_DEPARTMENTS.map((d) => [d.key, { labelPt: d.labelPt, labelEn: d.labelEn }]),
);

/**
 * Merge live catalog rows over the fallback constants. Unknown keys are
 * added; known keys have their labels refreshed. Never removes fallbacks.
 */
export const applyTaxonomyOverride = (payload: {
  seniorities?: Array<{ key: string; labelPt?: string; labelEn?: string }>;
  departments?: Array<{ key: string; labelPt?: string; labelEn?: string }>;
}): void => {
  for (const s of payload.seniorities ?? []) {
    if (!s?.key) continue;
    const prev = seniorityMap.get(s.key);
    seniorityMap.set(s.key, {
      labelPt: s.labelPt || prev?.labelPt || s.key,
      labelEn: s.labelEn || prev?.labelEn || s.labelPt || s.key,
    });
  }
  for (const d of payload.departments ?? []) {
    if (!d?.key) continue;
    const prev = departmentMap.get(d.key);
    departmentMap.set(d.key, {
      labelPt: d.labelPt || prev?.labelPt || d.key,
      labelEn: d.labelEn || prev?.labelEn || d.labelPt || d.key,
    });
  }
};

const pick = (entry: LabelPair | undefined, lang: TaxonomyLang, key: string): string => {
  if (!entry) return key;
  return lang === 'en' ? entry.labelEn : entry.labelPt;
};

/** Seniority label for a key. Defaults to Portuguese. */
export const getSeniorityLabel = (key: string, lang: TaxonomyLang = 'pt'): string =>
  pick(seniorityMap.get(key), lang, key);

/** Department label for a key. Defaults to Portuguese. */
export const getDeptLabel = (key: string, lang: TaxonomyLang = 'pt'): string =>
  pick(departmentMap.get(key), lang, key);
